import { useEffect, useMemo, useState } from 'react';
import { CirclePlus, FileSearch, History, ShieldCheck } from 'lucide-react';
import PageHeader from '@/components/PageHeader.jsx';
import EmptyState from '@/components/common/EmptyState.jsx';
import StickyActions from '@/components/common/StickyActions.jsx';
import { useAuth } from '@/state/auth/AuthContext.jsx';
import {
  fetchTariffAudit,
  fetchTariffWorkbench,
  saveTariffConcept,
  saveTariffList,
  saveTariffNumerator,
  saveTariffRule,
  simulateTariff,
} from '@/services/tariffWorkbench.js';
import CoefficientAdjustmentPanel from './components/CoefficientAdjustmentPanel.jsx';
import RuleForm from './components/RuleForm.jsx';
import SimpleRuleForm from './components/SimpleRuleForm.jsx';
import SimulatorPanel from './components/SimulatorPanel.jsx';
import StepperTarifas from './components/StepperTarifas.jsx';
import TariffModuleErrorBoundary from './components/TariffModuleErrorBoundary.jsx';
import CollapsibleSection from './components/CollapsibleSection.jsx';
import { buttonClass, fieldClass, LabeledField, parseJsonSafe, shellCard } from './components/shared.jsx';
import TariffAssistantPanel from '@/modules/tarifas/components/TariffAssistantPanel.jsx';
import {
  buildRuleCode,
  buildRuleName,
  buildRuleSummary,
  buildSimulationDraft,
  getSimpleCalculationConfig,
  getSuggestedPriority,
  inferSimpleCalculationType,
  resolveScopeLabel,
  suggestSimpleCalculationType,
} from './components/ruleUx.js';

const tabs = ['conceptos', 'numeradores', 'listas', 'reglas', 'ajustes', 'simulador', 'auditoria'];

const tabMeta = {
  conceptos: { label: 'Conceptos', summary: 'Definí qué conceptos aparecen en el detalle valorizado.', dependsOn: 'catálogo funcional del negocio', next: 'Numeradores' },
  numeradores: { label: 'Numeradores', summary: 'Ordenan y segmentan esquemas comerciales.', dependsOn: 'conceptos estables', next: 'Listas' },
  listas: { label: 'Listas', summary: 'Congelan vigencias y contexto comercial.', dependsOn: 'conceptos y numeradores', next: 'Reglas' },
  reglas: { label: 'Reglas', summary: 'Definen cómo valorizar cada concepto según el universo operativo.', dependsOn: 'lista / versión vigente', next: 'Simulación' },
  ajustes: { label: 'Ajustes', summary: 'Crea una nueva versión desde una existente sin alterar el histórico.', dependsOn: 'lista, versión y reglas cargadas', next: 'Simulación' },
  simulador: { label: 'Simulación', summary: 'Prueba datos tipo guía y explica por qué gana una regla.', dependsOn: 'reglas cargadas', next: 'Historial' },
  auditoria: { label: 'Historial y auditoría', summary: 'Muestra cambios, simulaciones y trazabilidad funcional.', dependsOn: 'actividad del módulo', next: 'Revisión operativa' },
};

const baseList = {
  id: '', codigo: '', nombre: '', descripcion: '', alcance: 'GENERAL', estado: 'BORRADOR', activo: true,
  versionId: '', versionNombre: '', vigenciaDesde: new Date().toISOString().slice(0, 10), vigenciaHasta: '', numeradorId: '', notas: '',
};

const baseRule = {
  id: '', versionId: '', conceptId: '', codigo: '', nombre: '', descripcion: '', prioridad: 100, clienteId: '', subcuentaId: '',
  servicioCodigo: '', origenSucursalId: '', destinoSucursalId: '', origenLocalidadId: '', destinoLocalidadId: '', numeradorId: '',
  unidadBase: '', metodoCalculo: '', valorUnitario: 0, porcentaje: 0, minimo: 0, baseDesde: '', baseHasta: '', observaciones: '', activo: true,
};

const baseSimulation = {
  fecha: new Date().toISOString().slice(0, 10),
  clienteId: '',
  subcuentaId: '',
  servicioCodigo: '',
  numeradorId: '',
  origenSucursalId: '',
  destinoSucursalId: '',
  origenLocalidadId: '',
  destinoLocalidadId: '',
  kg: 10,
  bultos: 1,
  m3: 0,
  kgAforado: 0,
  valorDeclarado: 0,
  contrareembolso: 0,
};

function SimpleMasterSection({ title, description, children, table, assistant = null }) {
  return (
    <section className={`${shellCard} p-5`}>
      <div className={`grid gap-5 ${assistant ? 'xl:grid-cols-[minmax(0,1.45fr)_320px]' : ''}`}>
        <div>
          <h2 className="text-base font-semibold text-[rgb(var(--fg))]">{title}</h2>
          <p className="mt-1 text-sm text-[rgba(var(--muted),0.95)]">{description}</p>
          <div className="mt-4">{children}</div>
          <div className="mt-6 overflow-x-auto">{table}</div>
        </div>
        {assistant}
      </div>
    </section>
  );
}

function getAuditSummary(item) {
  const after = parseJsonSafe(item.afterJson);
  const before = parseJsonSafe(item.beforeJson);
  return after?.codigo || after?.nombre || before?.codigo || before?.nombre || after?.ruleCode || item.processRunId || 'Cambio registrado';
}

function getAuditHumanTitle(item) {
  const actionMap = { create: 'Regla o entidad creada', update: 'Entidad actualizada', revalue: 'Revalorización ejecutada', simulate: 'Simulación ejecutada' };
  return actionMap[item.accion] || item.accion || 'Movimiento registrado';
}

function diffKeys(before, after) {
  if (!before || !after || typeof before !== 'object' || typeof after !== 'object') return [];
  return Object.keys(after).filter((key) => JSON.stringify(before[key]) !== JSON.stringify(after[key]));
}

function normalizeClienteId(value) {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}

function buildSimulationPayload(simulationForm = {}) {
  const clienteId = normalizeClienteId(simulationForm?.clienteId);
  if (!clienteId) return null;
  return {
    ...simulationForm,
    clienteId,
  };
}

export default function TarifasPage({ defaultTab = 'conceptos' }) {
  const { tenant, token } = useAuth();
  const [tab, setTab] = useState(defaultTab);
  const [ruleMode, setRuleMode] = useState('simple');
  const [showCircuit, setShowCircuit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [simulating, setSimulating] = useState(false);
  const [message, setMessage] = useState('');
  const [simulatorError, setSimulatorError] = useState('');
  const [data, setData] = useState({ conceptos: [], numeradores: [], listas: [], reglas: [] });
  const [ruleClientMode, setRuleClientMode] = useState('all');
  const [selectedRuleCliente, setSelectedRuleCliente] = useState(null);
  const [selectedSimulationCliente, setSelectedSimulationCliente] = useState(null);
  const [simulation, setSimulation] = useState(null);
  const [auditRows, setAuditRows] = useState([]);
  const [renderGuardKey, setRenderGuardKey] = useState(0);
  const [priorityTouched, setPriorityTouched] = useState(false);
  const [lastSavedRule, setLastSavedRule] = useState(null);
  const [forms, setForms] = useState({
    concepto: { id: '', codigo: '', nombre: '', descripcion: '', tipoConcepto: 'FLETE', orden: 10, naturaleza: 'SUMA', afectaTotal: true, afectaFacturacion: true, afectaCobranza: false, afectaLiquidacion: false, activo: true },
    numerador: { id: '', codigo: '', nombre: '', descripcion: '', prefijo: '', longitud: 8, siguienteNumero: 1, reinicioAnual: false, activo: true },
    lista: baseList,
    regla: baseRule,
    simulacion: baseSimulation,
    auditoria: { entidadTipo: '', accion: '', usuarioId: '', fechaDesde: '', fechaHasta: '', entidadId: '', processRunId: '', search: '' },
  });

  const ruleDirty = useMemo(() => JSON.stringify(forms.regla) !== JSON.stringify(baseRule), [forms.regla]);
  const stepStatus = useMemo(() => tabs.map((currentTab) => ({
    tab: currentTab,
    title: tabMeta[currentTab].label,
    summary: tabMeta[currentTab].summary,
    dependsOn: tabMeta[currentTab].dependsOn,
    next: tabMeta[currentTab].next,
    countLabel: currentTab === 'conceptos'
      ? `${data.conceptos.length} registros`
      : currentTab === 'numeradores'
        ? `${data.numeradores.length} registros`
        : currentTab === 'listas'
          ? `${data.listas.length} registros`
          : currentTab === 'reglas'
            ? `${data.reglas.length} reglas`
            : currentTab === 'ajustes'
              ? 'proceso versionado'
              : currentTab === 'simulador'
              ? simulation ? `${simulation.detalles?.length || 0} conceptos simulados` : 'sin prueba todavía'
              : auditRows.length ? `${auditRows.length} movimientos` : 'sin movimientos cargados',
    state: currentTab === 'ajustes'
      ? (data.listas.length ? 'completo' : 'incompleto')
      : currentTab === 'simulador'
      ? (simulation ? 'completo' : 'incompleto')
      : currentTab === 'auditoria'
        ? (auditRows.length ? 'completo' : 'vacio')
        : currentTab === 'reglas'
          ? (data.reglas.length ? 'completo' : 'incompleto')
          : (currentTab === 'conceptos' ? data.conceptos.length : currentTab === 'numeradores' ? data.numeradores.length : data.listas.length) ? 'completo' : 'vacio',
  })), [auditRows.length, data, simulation]);

  const listOptions = useMemo(
    () => data.listas.map((item) => ({ value: item.versionActualId, label: `${item.codigo} / v${item.versionActualNumero || '-'} ${item.versionActualNombre || ''}`.trim() })).filter((item) => item.value),
    [data.listas],
  );
  const coefficientVersions = useMemo(() => {
    const versions = new Map();
    data.listas.forEach((list) => {
      if (!list.versionActualId) return;
      versions.set(String(list.versionActualId), {
        id: list.versionActualId,
        listaId: list.id,
        listaLabel: `${list.codigo} · ${list.nombre}`,
        label: `${list.codigo} · ${list.nombre} / v${list.versionActualNumero || '-'} ${list.versionActualNombre || ''}`.trim(),
      });
    });
    data.reglas.forEach((rule) => {
      if (!rule.versionId || versions.has(String(rule.versionId))) return;
      versions.set(String(rule.versionId), {
        id: rule.versionId,
        listaId: rule.listaId,
        listaLabel: `${rule.listaCodigo || 'Lista'} · ${rule.listaNombre || ''}`.trim(),
        label: `${rule.listaCodigo || 'Lista'} · ${rule.listaNombre || ''} / v${rule.versionNumero || '-'} ${rule.versionNombre || ''}`.trim(),
      });
    });
    return Array.from(versions.values()).sort((a, b) => String(a.listaLabel).localeCompare(String(b.listaLabel)) || Number(b.id) - Number(a.id));
  }, [data.listas, data.reglas]);
  const activeVersionLabel = useMemo(() => listOptions.find((item) => String(item.value) === String(forms.regla.versionId))?.label || '', [forms.regla.versionId, listOptions]);
  const compactCircuit = 'Conceptos → Numeradores → Listas → Reglas → Simulación';
  const ruleErrors = useMemo(() => ({
    versionId: forms.regla.versionId ? '' : 'Seleccioná lista / versión.',
    conceptId: forms.regla.conceptId ? '' : 'Seleccioná un concepto.',
    unidadBase: forms.regla.unidadBase ? '' : 'Definí la base de cálculo.',
    metodoCalculo: forms.regla.metodoCalculo ? '' : 'Definí el método de cálculo.',
  }), [forms.regla]);
  const selectedConcept = useMemo(
    () => data.conceptos.find((item) => String(item.id) === String(forms.regla.conceptId)) || null,
    [data.conceptos, forms.regla.conceptId],
  );
  const simpleCalculationType = useMemo(() => inferSimpleCalculationType(forms.regla), [forms.regla]);
  const serviceOptions = useMemo(() => {
    const codes = new Set();
    data.reglas.forEach((item) => {
      if (item?.servicioCodigo) codes.add(item.servicioCodigo);
    });
    if (forms.regla.servicioCodigo) codes.add(forms.regla.servicioCodigo);
    if (forms.simulacion.servicioCodigo) codes.add(forms.simulacion.servicioCodigo);
    return Array.from(codes).sort().map((item) => ({ value: item, label: item }));
  }, [data.reglas, forms.regla.servicioCodigo, forms.simulacion.servicioCodigo]);
  const locationOptions = useMemo(() => {
    const rows = [];
    data.reglas.forEach((item) => {
      if (item?.origenLocalidadId && item?.origenLocalidadNombre) rows.push({ value: item.origenLocalidadId, label: item.origenLocalidadNombre });
      if (item?.destinoLocalidadId && item?.destinoLocalidadNombre) rows.push({ value: item.destinoLocalidadId, label: item.destinoLocalidadNombre });
    });
    const dedupe = new Map();
    rows.forEach((item) => {
      if (!dedupe.has(String(item.value))) dedupe.set(String(item.value), item);
    });
    return Array.from(dedupe.values()).sort((a, b) => String(a.label).localeCompare(String(b.label)));
  }, [data.reglas]);
  const scopeLabel = useMemo(() => resolveScopeLabel(forms.regla), [forms.regla]);
  const ruleSummary = useMemo(() => buildRuleSummary({
    rule: forms.regla,
    conceptLabel: selectedConcept?.nombre || selectedConcept?.codigo || 'Regla',
    labels: {
      cliente: selectedRuleCliente?.label || selectedRuleCliente?.nombre || selectedRuleCliente?.razonSocial || '',
      servicio: forms.regla.servicioCodigo || '',
      origen: locationOptions.find((item) => String(item.value) === String(forms.regla.origenLocalidadId))?.label || '',
      destino: locationOptions.find((item) => String(item.value) === String(forms.regla.destinoLocalidadId))?.label || '',
    },
  }), [forms.regla, locationOptions, selectedConcept, selectedRuleCliente]);
  const visibleRuleErrors = useMemo(() => ({
    ...ruleErrors,
    value: (
      forms.regla.metodoCalculo === 'PORCENTAJE'
        ? Number(forms.regla.porcentaje || 0) <= 0
        : Number(forms.regla.valorUnitario || 0) <= 0
    ) ? 'Ingresá un valor mayor a cero.' : '',
  }), [forms.regla, ruleErrors]);
  const conceptAssistantState = useMemo(() => ({
    assistantKind: 'conceptos',
    ...forms.concepto,
  }), [forms.concepto]);
  const numeratorAssistantState = useMemo(() => ({
    assistantKind: 'numeradores',
    ...forms.numerador,
  }), [forms.numerador]);
  const listAssistantState = useMemo(() => ({
    assistantKind: 'listas',
    ...forms.lista,
  }), [forms.lista]);
  const ruleAssistantState = useMemo(() => ({
    assistantKind: 'reglas',
    ...forms.regla,
    selectedClienteLabel: selectedRuleCliente?.label || selectedRuleCliente?.nombre || selectedRuleCliente?.razonSocial || '',
    selectedConceptLabel: selectedConcept?.nombre || selectedConcept?.codigo || '',
    activeVersionLabel,
    ruleMode,
  }), [activeVersionLabel, forms.regla, ruleMode, selectedConcept, selectedRuleCliente]);

  const loadWorkbench = async () => {
    setLoading(true);
    try {
      const payload = await fetchTariffWorkbench({ tenant, token });
      setData(payload);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { setTab(defaultTab); }, [defaultTab]);
  useEffect(() => { loadWorkbench(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (forms.regla.clienteId) setRuleClientMode('specific');
  }, [forms.regla.clienteId]);

  useEffect(() => {
    if (!forms.regla.versionId && listOptions.length) {
      setForms((prev) => ({ ...prev, regla: { ...prev.regla, versionId: listOptions[0].value } }));
    }
  }, [forms.regla.versionId, listOptions]);

  useEffect(() => {
    if (!selectedConcept || forms.regla.id) return;
    const suggestion = getSimpleCalculationConfig(suggestSimpleCalculationType(selectedConcept));
    setForms((prev) => ({
      ...prev,
      regla: {
        ...prev.regla,
        unidadBase: prev.regla.unidadBase || suggestion.unidadBase,
        metodoCalculo: prev.regla.metodoCalculo || suggestion.metodoCalculo,
      },
    }));
  }, [forms.regla.id, selectedConcept]);

  useEffect(() => {
    if (priorityTouched || forms.regla.id) return;
    const suggested = getSuggestedPriority(forms.regla);
    if (Number(forms.regla.prioridad || 0) === suggested) return;
    setForms((prev) => ({ ...prev, regla: { ...prev.regla, prioridad: suggested } }));
  }, [forms.regla, priorityTouched]);

  useEffect(() => {
    if (!selectedConcept || forms.regla.id) return;
    setForms((prev) => ({
      ...prev,
      regla: {
        ...prev.regla,
        codigo: buildRuleCode({ concept: selectedConcept, simpleCalculationType, form: prev.regla }),
        nombre: buildRuleName({
          concept: selectedConcept,
          simpleCalculationType,
          form: prev.regla,
          labels: {
            cliente: selectedRuleCliente?.label || selectedRuleCliente?.nombre || selectedRuleCliente?.razonSocial || '',
            servicio: prev.regla.servicioCodigo || '',
            origen: locationOptions.find((item) => String(item.value) === String(prev.regla.origenLocalidadId))?.label || '',
            destino: locationOptions.find((item) => String(item.value) === String(prev.regla.destinoLocalidadId))?.label || '',
          },
        }),
      },
    }));
  }, [
    forms.regla.id,
    forms.regla.conceptId,
    forms.regla.clienteId,
    forms.regla.servicioCodigo,
    forms.regla.origenLocalidadId,
    forms.regla.destinoLocalidadId,
    locationOptions,
    selectedConcept,
    selectedRuleCliente,
    simpleCalculationType,
  ]);

  useEffect(() => {
    if (tab === 'auditoria' && !auditRows.length) {
      fetchTariffAudit({ tenant, token, filters: forms.auditoria })
        .then(setAuditRows)
        .catch((error) => setMessage(error.message));
    }
  }, [auditRows.length, forms.auditoria, tab, tenant, token]);

  const updateForm = (group, key, value) => setForms((prev) => ({ ...prev, [group]: { ...prev[group], [key]: value } }));

  const updateRuleForm = (key, value) => {
    if (
      ['versionId', 'conceptId'].includes(key)
      && ruleDirty
      && forms.regla[key]
      && String(forms.regla[key]) !== String(value)
      && !window.confirm('Hay cambios sin guardar en la regla. Si cambiás lista o concepto, el contexto actual puede quedar inconsistente. ¿Querés continuar?')
    ) {
      return;
    }

    if (key === 'prioridad') setPriorityTouched(true);
    setForms((prev) => ({
      ...prev,
      regla: {
        ...prev.regla,
        [key]: value,
        ...(key === 'metodoCalculo' && value !== 'PORCENTAJE' ? { porcentaje: 0 } : {}),
        ...(key === 'metodoCalculo' && value === 'PORCENTAJE' ? { valorUnitario: 0 } : {}),
      },
    }));
  };

  const resetRule = () => {
    setForms((prev) => ({ ...prev, regla: baseRule }));
    setRuleClientMode('all');
    setSelectedRuleCliente(null);
    setPriorityTouched(false);
  };

  const resetSimulation = () => {
    setSimulation(null);
    setSimulatorError('');
    setSelectedSimulationCliente(null);
    setForms((prev) => ({ ...prev, simulacion: baseSimulation }));
  };

  const setTabAndClearMessage = (nextTab) => {
    setMessage('');
    setTab(nextTab);
  };

  const saveMaster = async (kind) => {
    setSaving(true);
    try {
      if (kind === 'concepto') await saveTariffConcept({ tenant, token, body: forms.concepto });
      if (kind === 'numerador') await saveTariffNumerator({ tenant, token, body: forms.numerador });
      if (kind === 'lista') {
        await saveTariffList({
          tenant,
          token,
          body: {
            ...forms.lista,
            version: {
              id: forms.lista.versionId || undefined,
              nombre: forms.lista.versionNombre || `${forms.lista.nombre} vigente`,
              vigenciaDesde: forms.lista.vigenciaDesde,
              vigenciaHasta: forms.lista.vigenciaHasta || null,
              estado: forms.lista.estado,
              numeradorId: forms.lista.numeradorId || null,
              notas: forms.lista.notas || null,
            },
          },
        });
      }

      await loadWorkbench();
      setMessage(`Se guardó ${kind} correctamente.`);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setSaving(false);
    }
  };

  const saveRule = async () => {
    if (Object.values(visibleRuleErrors).some(Boolean)) {
      setMessage('Completá lista / versión, concepto, base y método antes de guardar la regla.');
      return;
    }

    setSaving(true);
    try {
      const savedRule = await saveTariffRule({ tenant, token, body: forms.regla });
      await loadWorkbench();
      setLastSavedRule({
        id: savedRule?.id || null,
        summary: ruleSummary,
        rule: { ...forms.regla },
        selectedCliente: selectedRuleCliente,
      });
      setMessage('Se guardó la regla correctamente.');
      resetRule();
    } catch (error) {
      setMessage(error.message);
    } finally {
      setSaving(false);
    }
  };

  const runSimulation = async () => {
    const payload = buildSimulationPayload(forms.simulacion);
    if (!payload) {
      setSimulation(null);
      setSimulatorError('Seleccioná un cliente para simular la valorización.');
      setTab('simulador');
      return;
    }

    setSimulating(true);
    setSimulatorError('');
    try {
      const result = await simulateTariff({ tenant, token, body: payload });
      setSimulation(result);
      setTab('simulador');
    } catch (error) {
      setSimulatorError(error.message);
      setTab('simulador');
    } finally {
      setSimulating(false);
    }
  };

  const runSimulationWithDraft = async (rule = forms.regla, selectedCliente = null) => {
    const nextDraft = buildSimulationDraft(rule, forms.simulacion);
    setForms((prev) => ({ ...prev, simulacion: nextDraft }));
    setSelectedSimulationCliente(selectedCliente || null);
    setSimulation(null);
    setTab('simulador');

    const payload = buildSimulationPayload(nextDraft);
    if (!payload) {
      setSimulatorError('Seleccioná un cliente para simular la valorización.');
      return;
    }

    setSimulating(true);
    setSimulatorError('');
    try {
      const result = await simulateTariff({ tenant, token, body: payload });
      setSimulation(result);
    } catch (error) {
      setSimulatorError(error.message);
    } finally {
      setSimulating(false);
    }
  };

  const moveRuleContextToSimulation = () => {
    setForms((prev) => ({ ...prev, simulacion: buildSimulationDraft(prev.regla, prev.simulacion) }));
    setSelectedSimulationCliente(selectedRuleCliente || null);
    setTab('simulador');
  };

  const loadAudit = async () => {
    try {
      const rows = await fetchTariffAudit({ tenant, token, filters: forms.auditoria });
      setAuditRows(rows);
      setTab('auditoria');
    } catch (error) {
      setMessage(error.message);
    }
  };

  return (
    <div className="min-h-screen bg-[rgba(var(--bg),1)]">
      <section className="mx-auto max-w-[1400px] space-y-3 px-4 pb-6 pt-2 md:px-5">
        <PageHeader
          title="Tarifas"
          subtitle="ABM oficial del motor tarifario canónico usado en emisión para configurar, validar y auditar cómo queda valorizada una guía por concepto."
          actions={(
            <div className="flex flex-wrap gap-2">
              <button type="button" className={buttonClass} onClick={() => setTabAndClearMessage('listas')}>
                <CirclePlus className="mr-2 h-4 w-4" />Nueva lista
              </button>
              <button type="button" className={buttonClass} onClick={() => setTabAndClearMessage('reglas')}>
                <ShieldCheck className="mr-2 h-4 w-4" />Nueva regla
              </button>
              <button type="button" className={buttonClass} onClick={() => setTabAndClearMessage('ajustes')}>
                <ShieldCheck className="mr-2 h-4 w-4" />Ajustar por coeficiente
              </button>
              <button type="button" className={buttonClass} onClick={moveRuleContextToSimulation}>
                <FileSearch className="mr-2 h-4 w-4" />Simular
              </button>
              <button type="button" className={buttonClass} onClick={loadAudit}>
                <History className="mr-2 h-4 w-4" />Ver historial
              </button>
            </div>
          )}
        />

        <CollapsibleSection
          title="Circuito de tarifas"
          description={compactCircuit}
          open={showCircuit}
          onToggle={() => setShowCircuit((prev) => !prev)}
        >
          <div className="space-y-4">
            <StepperTarifas steps={stepStatus} active={tab} onSelect={setTab} />

            <section className={`${shellCard} p-4`}>
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <p className="text-sm font-semibold text-[rgb(var(--fg))]">{tabMeta[tab].label}</p>
                  <p className="mt-1 text-sm text-[rgba(var(--fg),0.82)]">{tabMeta[tab].summary}</p>
                  <p className="mt-2 text-xs text-[rgba(var(--muted),0.95)]">Depende de {tabMeta[tab].dependsOn} y sigue con {tabMeta[tab].next}.</p>
                </div>
                <div className="rounded-2xl border border-[rgba(var(--border),0.35)] bg-[rgba(var(--subpanel),0.16)] px-4 py-3 text-sm text-[rgba(var(--fg),0.82)]">
                  Flujo recomendado: Conceptos {'→'} Numeradores {'→'} Listas {'→'} Reglas {'→'} Simulación {'→'} Historial
                </div>
              </div>
            </section>

            <section className={`${shellCard} p-4`}>
              <div className="grid gap-4 lg:grid-cols-[1.1fr_1fr]">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[rgba(var(--muted),0.95)]">ABM oficial</p>
                  <p className="mt-2 text-sm text-[rgba(var(--fg),0.84)]">
                    Esta vista es la referencia oficial para cargar el tarifario que luego consume la emisión y termina persistido en snapshot.
                  </p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[rgba(var(--muted),0.95)]">Circuito real</p>
                  <p className="mt-2 text-sm text-[rgba(var(--fg),0.84)]">
                    Concepto {'→'} Numerador {'→'} Lista {'→'} Versión {'→'} Asignación {'→'} Regla {'→'} Simulación {'→'} Snapshot
                  </p>
                </div>
              </div>
            </section>
          </div>
        </CollapsibleSection>

        {message ? <div className={`${shellCard} px-4 py-2 text-sm text-[rgb(var(--fg))]`}>{message}</div> : null}

        <section className={`${shellCard} p-3`}>
          <div className="flex flex-wrap gap-2">
            {tabs.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => setTab(item)}
                aria-current={tab === item ? 'page' : undefined}
                className={`rounded-full px-3 py-2 text-sm font-semibold transition ${tab === item ? 'bg-[rgba(var(--brand),0.14)] text-[rgb(var(--brand))]' : 'bg-[rgba(var(--subpanel),0.2)] text-[rgba(var(--fg),0.84)] hover:bg-[rgba(var(--subpanel),0.45)]'}`}
              >
                {tabMeta[item].label}
              </button>
            ))}
          </div>
        </section>

        {loading ? <div className={`${shellCard} p-6 text-sm text-[rgba(var(--muted),0.95)]`}>Cargando workbench...</div> : null}

        {!loading && tab === 'conceptos' ? (
          <SimpleMasterSection
            title="Conceptos valorizables"
            description="Primero definí el catálogo que después termina materializado por concepto en el snapshot de guía."
            assistant={<TariffAssistantPanel formState={conceptAssistantState} storageKey="tariff-assistant-conceptos" />}
            table={data.conceptos.length ? (
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-[rgba(var(--muted),0.95)]">
                    <th className="pb-2">Código</th>
                    <th className="pb-2">Nombre</th>
                    <th className="pb-2">Tipo</th>
                    <th className="pb-2">Orden</th>
                  </tr>
                </thead>
                <tbody>
                  {data.conceptos.map((item) => (
                    <tr key={item.id} className="border-t border-[rgba(var(--border),0.4)]">
                      <td className="py-2">{item.codigo}</td>
                      <td>{item.nombre}</td>
                      <td>{item.tipoConcepto}</td>
                      <td>{item.orden}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <EmptyState title="Sin conceptos" description="Cargá conceptos antes de avanzar a listas y reglas." />}
          >
            <div className="grid grid-cols-12 gap-4">
              <LabeledField label="Código interno"><input className={fieldClass} value={forms.concepto.codigo} onChange={(e) => updateForm('concepto', 'codigo', e.target.value)} placeholder="FLETE" /></LabeledField>
              <LabeledField label="Nombre"><input className={fieldClass} value={forms.concepto.nombre} onChange={(e) => updateForm('concepto', 'nombre', e.target.value)} placeholder="Flete" /></LabeledField>
              <LabeledField label="Tipo"><input className={fieldClass} value={forms.concepto.tipoConcepto} onChange={(e) => updateForm('concepto', 'tipoConcepto', e.target.value)} placeholder="FLETE / SEGURO / ENTREGA" /></LabeledField>
            </div>
            <StickyActions onCancel={() => setForms((prev) => ({ ...prev, concepto: { ...prev.concepto, id: '', codigo: '', nombre: '' } }))} onSubmit={() => saveMaster('concepto')} isSubmitting={saving} primaryLabel="Guardar concepto" secondaryLabel="Cancelar" />
          </SimpleMasterSection>
        ) : null}

        {!loading && tab === 'numeradores' ? (
          <SimpleMasterSection
            title="Numeradores"
            description="Prepará numeradores reutilizables para listas, versiones y segmentación comercial."
            assistant={<TariffAssistantPanel formState={numeratorAssistantState} storageKey="tariff-assistant-numeradores" />}
            table={data.numeradores.length ? (
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-[rgba(var(--muted),0.95)]">
                    <th className="pb-2">Código</th>
                    <th className="pb-2">Nombre</th>
                    <th className="pb-2">Próximo</th>
                  </tr>
                </thead>
                <tbody>
                  {data.numeradores.map((item) => (
                    <tr key={item.id} className="border-t border-[rgba(var(--border),0.4)]">
                      <td className="py-2">{item.codigo}</td>
                      <td>{item.nombre}</td>
                      <td>{item.siguienteNumero}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <EmptyState title="Sin numeradores" description="Creá al menos uno para soportar el circuito de listas y versiones." />}
          >
            <div className="grid grid-cols-12 gap-4">
              <LabeledField label="Código"><input className={fieldClass} value={forms.numerador.codigo} onChange={(e) => updateForm('numerador', 'codigo', e.target.value)} placeholder="TARCLI" /></LabeledField>
              <LabeledField label="Nombre"><input className={fieldClass} value={forms.numerador.nombre} onChange={(e) => updateForm('numerador', 'nombre', e.target.value)} placeholder="Numerador tarifario" /></LabeledField>
              <LabeledField label="Siguiente número"><input type="number" className={fieldClass} value={forms.numerador.siguienteNumero} onChange={(e) => updateForm('numerador', 'siguienteNumero', e.target.value)} /></LabeledField>
            </div>
            <StickyActions onCancel={() => setForms((prev) => ({ ...prev, numerador: { ...prev.numerador, id: '', codigo: '', nombre: '' } }))} onSubmit={() => saveMaster('numerador')} isSubmitting={saving} primaryLabel="Guardar numerador" secondaryLabel="Cancelar" />
          </SimpleMasterSection>
        ) : null}

        {!loading && tab === 'listas' ? (
          <SimpleMasterSection
            title="Listas y versiones"
            description="La lista / versión define el contexto comercial donde después viven las reglas y se congela la vigencia."
            assistant={<TariffAssistantPanel formState={listAssistantState} storageKey="tariff-assistant-listas" />}
            table={data.listas.length ? (
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-[rgba(var(--muted),0.95)]">
                    <th className="pb-2">Lista</th>
                    <th className="pb-2">Versión vigente</th>
                    <th className="pb-2">Vigencia</th>
                  </tr>
                </thead>
                <tbody>
                  {data.listas.map((item) => (
                    <tr key={item.id} className="border-t border-[rgba(var(--border),0.4)]">
                      <td className="py-2">{item.codigo} · {item.nombre}</td>
                      <td>v{item.versionActualNumero || '-'} {item.versionActualNombre || ''}</td>
                      <td>{item.vigenciaDesde || '-'} / {item.vigenciaHasta || 'abierta'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <EmptyState title="Sin listas" description="Creá una lista y una versión antes de cargar reglas." />}
          >
            <div className="grid grid-cols-12 gap-4">
              <LabeledField label="Código de lista"><input className={fieldClass} value={forms.lista.codigo} onChange={(e) => updateForm('lista', 'codigo', e.target.value)} placeholder="LCLI100" /></LabeledField>
              <LabeledField label="Nombre comercial"><input className={fieldClass} value={forms.lista.nombre} onChange={(e) => updateForm('lista', 'nombre', e.target.value)} placeholder="Tarifa cliente 100" /></LabeledField>
              <LabeledField label="Versión"><input className={fieldClass} value={forms.lista.versionNombre} onChange={(e) => updateForm('lista', 'versionNombre', e.target.value)} placeholder="Abril 2026" /></LabeledField>
            </div>
            <StickyActions onCancel={() => setForms((prev) => ({ ...prev, lista: baseList }))} onSubmit={() => saveMaster('lista')} isSubmitting={saving} primaryLabel="Guardar lista" secondaryLabel="Cancelar" />
          </SimpleMasterSection>
        ) : null}

        {!loading && tab === 'reglas' ? (
          ruleMode === 'simple' ? (
            <SimpleRuleForm
              form={forms.regla}
              concepts={data.conceptos}
              selectedCliente={selectedRuleCliente}
              onClienteSelect={(cliente) => {
                setSelectedRuleCliente(cliente);
                updateRuleForm('clienteId', cliente?.id ? String(cliente.id) : '');
              }}
              onChange={updateRuleForm}
              onSubmit={saveRule}
              onToggleMode={() => setRuleMode('advanced')}
              onTryRule={() => runSimulationWithDraft(lastSavedRule?.rule || forms.regla, lastSavedRule?.selectedCliente || selectedRuleCliente)}
              simpleCalculationType={simpleCalculationType}
              onSimpleCalculationTypeChange={(value) => {
                const config = getSimpleCalculationConfig(value);
                updateRuleForm('unidadBase', config.unidadBase);
                updateRuleForm('metodoCalculo', config.metodoCalculo);
                if (config.metodoCalculo === 'PORCENTAJE') updateRuleForm('valorUnitario', 0);
                else updateRuleForm('porcentaje', 0);
              }}
              scopeLabel={scopeLabel}
              priorityLabel={`${forms.regla.prioridad || 0}${priorityTouched ? '' : ' auto'}`}
              summaryText={ruleSummary}
              activeVersionLabel={activeVersionLabel}
              serviceOptions={serviceOptions}
              locationOptions={locationOptions}
              errors={visibleRuleErrors}
              isSubmitting={saving}
              canTryRule={Boolean(lastSavedRule)}
              lastSavedSummary={lastSavedRule?.summary || ''}
              assistantPanel={<TariffAssistantPanel formState={ruleAssistantState} storageKey="tariff-assistant-reglas-simple" />}
            />
          ) : (
            <RuleForm
              form={forms.regla}
              concepts={data.conceptos}
              numerators={data.numeradores}
              listOptions={listOptions}
              previewText={ruleSummary}
              selectedCliente={selectedRuleCliente}
              clientMode={ruleClientMode}
              onChange={updateRuleForm}
              onClienteSelect={(cliente) => {
                setSelectedRuleCliente(cliente);
                if (cliente?.id) {
                  updateRuleForm('clienteId', String(cliente.id));
                  setRuleClientMode('specific');
                } else {
                  updateRuleForm('clienteId', '');
                }
              }}
              onClientModeChange={(mode) => {
                setRuleClientMode(mode);
                if (mode === 'all') {
                  setSelectedRuleCliente(null);
                  updateRuleForm('clienteId', '');
                }
              }}
              onCancel={resetRule}
              onSubmit={saveRule}
              onSimulate={() => runSimulationWithDraft(forms.regla, selectedRuleCliente)}
              onHistory={loadAudit}
              onToggleMode={() => setRuleMode('simple')}
              isSubmitting={saving}
              disableSubmit={Object.values(visibleRuleErrors).some(Boolean)}
              errors={visibleRuleErrors}
              isDirty={ruleDirty}
              scopeLabel={scopeLabel}
              summaryText={ruleSummary}
              assistantPanel={<TariffAssistantPanel formState={ruleAssistantState} storageKey="tariff-assistant-reglas-advanced" />}
            />
          )
        ) : null}

        {!loading && tab === 'ajustes' ? (
          <CoefficientAdjustmentPanel
            versions={coefficientVersions}
            concepts={data.conceptos}
            tenant={tenant}
            token={token}
            onApplied={async () => {
              await loadWorkbench();
              setTab('auditoria');
              setAuditRows([]);
            }}
          />
        ) : null}

        {!loading && tab === 'simulador' ? (
          <TariffModuleErrorBoundary onRetry={() => setRenderGuardKey((prev) => prev + 1)}>
            <div key={renderGuardKey}>
              <SimulatorPanel
                form={forms.simulacion}
                selectedCliente={selectedSimulationCliente}
                onChange={(key, value) => updateForm('simulacion', key, value)}
                onClienteSelect={(cliente) => {
                  setSelectedSimulationCliente(cliente);
                  updateForm('simulacion', 'clienteId', cliente?.id ? Number(cliente.id) : '');
                }}
                onRun={runSimulation}
                onReset={resetSimulation}
                simulation={simulation}
                isRunning={simulating}
                errorMessage={simulatorError}
                onRetry={runSimulation}
                context={{
                  activeVersionLabel,
                  ruleCode: forms.regla.codigo,
                  ruleName: forms.regla.nombre,
                }}
              />
            </div>
          </TariffModuleErrorBoundary>
        ) : null}

        {!loading && tab === 'auditoria' ? (
          <section className="space-y-4">
            <div className={`${shellCard} p-5`}>
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <h2 className="text-base font-semibold text-[rgb(var(--fg))]">Historial y auditoría</h2>
                  <p className="mt-1 text-sm text-[rgba(var(--muted),0.95)]">
                    Esta vista muestra cambios operativos del dominio tarifario: altas, ediciones, simulaciones y trazabilidad para entender qué pasó y cuándo.
                  </p>
                </div>
                <button type="button" className={buttonClass} onClick={loadAudit}>Actualizar historial</button>
              </div>

              <div className="mt-4 grid grid-cols-12 gap-4">
                <LabeledField label="Entidad" span="col-span-12 md:col-span-3">
                  <input className={fieldClass} value={forms.auditoria.entidadTipo} onChange={(e) => updateForm('auditoria', 'entidadTipo', e.target.value)} placeholder="tariff_rule" />
                </LabeledField>
                <LabeledField label="Acción" span="col-span-12 md:col-span-3">
                  <input className={fieldClass} value={forms.auditoria.accion} onChange={(e) => updateForm('auditoria', 'accion', e.target.value)} placeholder="create / update / simulate" />
                </LabeledField>
                <LabeledField label="Usuario" span="col-span-12 md:col-span-3">
                  <input className={fieldClass} value={forms.auditoria.usuarioId} onChange={(e) => updateForm('auditoria', 'usuarioId', e.target.value)} placeholder="ID usuario" />
                </LabeledField>
                <LabeledField label="Regla / entidad ID" span="col-span-12 md:col-span-3">
                  <input className={fieldClass} value={forms.auditoria.entidadId} onChange={(e) => updateForm('auditoria', 'entidadId', e.target.value)} placeholder="123" />
                </LabeledField>
                <LabeledField label="Fecha desde" span="col-span-12 md:col-span-3">
                  <input type="date" className={fieldClass} value={forms.auditoria.fechaDesde} onChange={(e) => updateForm('auditoria', 'fechaDesde', e.target.value)} />
                </LabeledField>
                <LabeledField label="Fecha hasta" span="col-span-12 md:col-span-3">
                  <input type="date" className={fieldClass} value={forms.auditoria.fechaHasta} onChange={(e) => updateForm('auditoria', 'fechaHasta', e.target.value)} />
                </LabeledField>
                <LabeledField label="Proceso" span="col-span-12 md:col-span-2">
                  <input className={fieldClass} value={forms.auditoria.processRunId} onChange={(e) => updateForm('auditoria', 'processRunId', e.target.value)} placeholder="Run ID" />
                </LabeledField>
                <LabeledField label="Texto libre" span="col-span-12 md:col-span-4">
                  <input className={fieldClass} value={forms.auditoria.search} onChange={(e) => updateForm('auditoria', 'search', e.target.value)} placeholder="cliente, lista, regla, guía" />
                </LabeledField>
              </div>
            </div>

            {auditRows.length ? (
              <div className="space-y-3">
                {auditRows.map((item) => {
                  const after = parseJsonSafe(item.afterJson);
                  const before = parseJsonSafe(item.beforeJson);
                  const changedFields = diffKeys(before, after);
                  return (
                    <article key={item.id} className={`${shellCard} p-5`}>
                      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[rgba(var(--muted),0.95)]">{item.createdAt}</p>
                          <h3 className="mt-2 text-base font-semibold text-[rgb(var(--fg))]">{getAuditHumanTitle(item)}</h3>
                          <p className="mt-1 text-sm text-[rgba(var(--fg),0.84)]">{getAuditSummary(item)}</p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <span className="rounded-full border border-[rgba(var(--border),0.35)] bg-[rgba(var(--subpanel),0.16)] px-3 py-1 text-xs font-semibold text-[rgba(var(--fg),0.84)]">
                            {item.entidadTipo} #{item.entidadId || '-'}
                          </span>
                          <span className="rounded-full border border-[rgba(var(--border),0.35)] bg-[rgba(var(--subpanel),0.16)] px-3 py-1 text-xs font-semibold text-[rgba(var(--fg),0.84)]">
                            acción {item.accion}
                          </span>
                          {item.usuarioId ? (
                            <span className="rounded-full border border-[rgba(var(--border),0.35)] bg-[rgba(var(--subpanel),0.16)] px-3 py-1 text-xs font-semibold text-[rgba(var(--fg),0.84)]">
                              usuario {item.usuarioId}
                            </span>
                          ) : null}
                        </div>
                      </div>
                      <div className="mt-4 grid gap-3 xl:grid-cols-2">
                        <div className="rounded-2xl border border-[rgba(var(--border),0.35)] bg-[rgba(var(--panel),0.45)] p-4">
                          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[rgba(var(--muted),0.95)]">Qué pasó</p>
                          <p className="mt-2 text-sm text-[rgba(var(--fg),0.84)]">
                            {item.accion === 'create' ? 'Se dio de alta una nueva entidad tarifaria.' : item.accion === 'update' ? 'Se modificó una entidad existente.' : 'Se registró un movimiento del dominio tarifario.'}
                          </p>
                        </div>
                        <div className="rounded-2xl border border-[rgba(var(--border),0.35)] bg-[rgba(var(--panel),0.45)] p-4">
                          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[rgba(var(--muted),0.95)]">Campos modificados</p>
                          <div className="mt-2 flex flex-wrap gap-2">
                            {changedFields.length ? changedFields.map((field) => (
                              <span key={field} className="rounded-full border border-[rgba(var(--border),0.3)] bg-[rgba(var(--subpanel),0.16)] px-3 py-1 text-xs font-semibold text-[rgba(var(--fg),0.82)]">
                                {field}
                              </span>
                            )) : (
                              <span className="text-sm text-[rgba(var(--muted),0.95)]">Sin detalle estructurado de campos.</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            ) : (
              <EmptyState
                title="Todavía no hay movimientos para esta regla"
                description="Acá van a aparecer altas, ediciones, cambios de prioridad, activaciones, simulaciones y otros eventos del dominio tarifario."
              />
            )}
          </section>
        ) : null}
      </section>
    </div>
  );
}
